import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Box } from '@mui/material'
import { PlannedProductionTable } from './PlannedProduction.MUITable'
import {
  SkuEditEntry,
  useSavePlannedProduction,
  useSavePlannedProductionFreezeAllMixed,
  useSavePlannedProductionFreezeEdited,
  useUploadPlannedProduction,
  usePlannedProduction,
} from './PlannedProduction.data'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from 'Common/Supplyvue-ui'
import toast from 'react-hot-toast'
import { PlannedProductionData, WeekEditInfo } from './PlannedProduction.types'
import { useRunPlan } from 'Features/EditSettings/EditSettings.data'
import { RunPlanParameters } from 'Features/EditSettings/EditSettings.types'
import {
  FreezeOption,
  FreezeOptionsDialog,
  FreezeOptionsResult,
} from './FreezeOptionsDialog'

type SkuEditState = {
  workCentreCode: string
  skuCode: string
  maxWeekEditedIndex: number
}

const skuEditKey = (workCentreCode: string, skuCode: string) =>
  `${workCentreCode}||${skuCode}`

export const PlannedProductionView = () => {
  const { planId } = useParams<{ planId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: response, isLoading } = usePlannedProduction(planId as string)
  const { mutate: savePlannedProduction, isLoading: isSavingOpt1 } =
    useSavePlannedProduction()
  const { mutate: saveFreezeEdited, isLoading: isSavingOpt2 } =
    useSavePlannedProductionFreezeEdited()
  const { mutate: saveFreezeAllMixed, isLoading: isSavingOpt3 } =
    useSavePlannedProductionFreezeAllMixed()
  const { mutate: uploadPlannedProduction, isLoading: isUploading } =
    useUploadPlannedProduction()

  const isSaving = isSavingOpt1 || isSavingOpt2 || isSavingOpt3

  const [showAllWorkcenters, setShowAllWorkcenters] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [changesApplied, setChangesApplied] = useState(false)
  const dataToSendToServer = useRef<PlannedProductionData[]>([])
  const farthestEditedWeekRef = useRef<number>(-1)
  const editedSkusRef = useRef<Map<string, SkuEditState>>(new Map())

  const [tableData, setTableData] = useState<PlannedProductionData[]>([])

  const [runPlanParams, setRunPlanParams] = useState<RunPlanParameters>({
    extractData: false,
    unconstrainedPlan: true,
  })

  const onPlanRunInitiatedSuccess = () => {
    navigate('/')
  }

  const onPlanRunInitiatedError = (message: string) => {
    toast.error(message)
  }

  const { mutate: run, isLoading: isRunning } = useRunPlan(
    onPlanRunInitiatedSuccess,
    onPlanRunInitiatedError
  )

  const handleRunPlan = () => {
    setRunPlanParams({
      ...runPlanParams,
      unconstrainedPlan: true,
    })

    if (planId) {
      run({
        planId: planId,
        parameters: runPlanParams,
      })
    }
  }

  const resetEditTracking = () => {
    editedSkusRef.current = new Map()
    farthestEditedWeekRef.current = -1
  }

  useEffect(() => {
    if (response?.data) {
      const baseFilteredData =
        showAllWorkcenters === false
          ? response?.data.filter((item) => item.isPrimaryWorkCentre)
          : response?.data
      setTableData(baseFilteredData ?? [])
      setChangesApplied(false)
      resetEditTracking()
    }
  }, [response])

  const handleWorkCentreButtonClick = () => {
    setShowAllWorkcenters(!showAllWorkcenters)
  }

  const handleDialogClose = () => {
    if (!isSaving) {
      setDialogOpen(false)
      setChangesApplied(false)
      dataToSendToServer.current = []
      resetEditTracking()
      queryClient.resetQueries(['PlannedProduction', planId])
    }
  }

  const handleWeekEdited = (week: number, info: WeekEditInfo) => {
    farthestEditedWeekRef.current = Math.max(
      farthestEditedWeekRef.current,
      week
    )
    if (!info?.weekIndex) return
    const key = skuEditKey(info.workCentreCode, info.skuCode)
    const existing = editedSkusRef.current.get(key)
    if (!existing || info.weekIndex > existing.maxWeekEditedIndex) {
      editedSkusRef.current.set(key, {
        workCentreCode: info.workCentreCode,
        skuCode: info.skuCode,
        maxWeekEditedIndex: info.weekIndex,
      })
    }
  }

  const onDataSaveInTheTable = (updatedData: PlannedProductionData[]) => {
    setDialogOpen(true)
    setChangesApplied(false)
    dataToSendToServer.current = [...updatedData]
  }

  const weeks = response?.weeks ?? []

  const weekOptions = useMemo(
    () =>
      (weeks as string[]).map((_w: string, index: number) => ({
        key: `${index + 1} week${index === 0 ? '' : 's'}`,
        value: index + 1,
      })),
    [weeks]
  )

  const initialWeeksForDialog = useMemo(() => {
    if (farthestEditedWeekRef.current === -1) return 1
    const idx = weeks.indexOf(String(farthestEditedWeekRef.current))
    return idx === -1 ? 1 : idx + 1
  }, [dialogOpen, weeks])

  const buildSkuEntries = (
    skuFilter: (row: PlannedProductionData) => boolean
  ): SkuEditEntry[] => {
    return dataToSendToServer.current.filter(skuFilter).map((row) => {
      const weekValues: Record<string, number> = {}
      for (const week of weeks) {
        const v = row[week]
        weekValues[week] = typeof v === 'number' ? v : Number(v) || 0
      }
      const key = skuEditKey(String(row.workCentreCode), String(row.skuCode))
      const editState = editedSkusRef.current.get(key)
      return {
        workCentreCode: String(row.workCentreCode),
        workCentreKey: Number(row.workCentreKey),
        skuCode: String(row.skuCode),
        skuKey: Number(row.skuKey),
        baseSourceSiteCode: String(row.baseSourceSiteCode),
        baseSourceSiteKey: Number(row.baseSourceSiteKey),
        maxWeekEditedIndex: editState?.maxWeekEditedIndex ?? null,
        weekValues,
      }
    })
  }

  const handleOption1 = (selectedWeeks: number) => {
    if (!planId) return
    const filteredData = dataToSendToServer.current.map((row) => {
      const filteredRow = { ...row }
      ;(weeks as string[]).slice(selectedWeeks).forEach((week: string) => {
        delete filteredRow[week]
      })
      return filteredRow
    })
    const selectedWeeksArray = weeks.slice(0, selectedWeeks)
    savePlannedProduction(
      { planId, data: filteredData, selectedWeeks: selectedWeeksArray },
      {
        onSuccess: onSaveSuccess,
        onError: onSaveError,
      }
    )
  }

  const handleOption2 = () => {
    if (!planId) return
    const editedKeys = new Set(editedSkusRef.current.keys())
    if (editedKeys.size === 0) {
      toast.error('No SKU edits detected — choose a different option.')
      return
    }
    const edits = buildSkuEntries((row) =>
      editedKeys.has(
        skuEditKey(String(row.workCentreCode), String(row.skuCode))
      )
    )
    saveFreezeEdited(
      { planId, payload: { edits } },
      { onSuccess: onSaveSuccess, onError: onSaveError }
    )
  }

  const handleOption3 = (minWeeks: number) => {
    if (!planId) return
    const rows = buildSkuEntries(() => true)
    saveFreezeAllMixed(
      { planId, payload: { minWeeks, rows } },
      { onSuccess: onSaveSuccess, onError: onSaveError }
    )
  }

  const onSaveSuccess = () => {
    setDialogOpen(false)
    toast.success('Changes applied successfully')
    setChangesApplied(true)
    dataToSendToServer.current = []
    resetEditTracking()
  }

  const onSaveError = (error: unknown) => {
    toast.error('Failed to save changes')
    console.error('Error saving data:', error)
  }

  const handleDialogConfirm = (result: FreezeOptionsResult) => {
    if (isSaving) return
    const dispatch: Record<FreezeOption, () => void> = {
      'all-min': () => handleOption1(result.weeks ?? 1),
      'edited-only': () => handleOption2(),
      'all-mixed': () => handleOption3(result.weeks ?? 1),
    }
    dispatch[result.option]()
  }

  const handleFileUpload = (file: File) => {
    if (planId) {
      uploadPlannedProduction(
        { planId, file },
        {
          onSuccess: (response) => {
            toast.success('File uploaded successfully')
            if (response?.data) {
              const merged = response?.data?.data ?? []
              const baseFilteredData =
                showAllWorkcenters === false
                  ? merged.filter(
                      (item: PlannedProductionData) => item.isPrimaryWorkCentre
                    )
                  : merged
              setTableData(baseFilteredData ?? [])
              dataToSendToServer.current = [...baseFilteredData]
              resetEditTracking()
              const original = response?.data?.originalData ?? []
              diffMergedAgainstOriginal(baseFilteredData, original)
              setChangesApplied(false)
              setDialogOpen(true)
            }
          },
          onError: (error) => {
            toast.error('Failed to upload file')
            console.error('Upload error:', error)
          },
        }
      )
    }
  }

  const diffMergedAgainstOriginal = (
    merged: PlannedProductionData[],
    original: PlannedProductionData[]
  ) => {
    if (!original.length) return
    const origByKey = new Map<string, PlannedProductionData>()
    original.forEach((row) => {
      origByKey.set(
        skuEditKey(String(row.workCentreCode), String(row.skuCode)),
        row
      )
    })
    merged.forEach((row) => {
      const key = skuEditKey(String(row.workCentreCode), String(row.skuCode))
      const orig = origByKey.get(key)
      if (!orig) return
      let maxIdx = 0
      ;(weeks as string[]).forEach((week: string, idx: number) => {
        if (row[week] !== orig[week]) {
          maxIdx = Math.max(maxIdx, idx + 1)
          farthestEditedWeekRef.current = Math.max(
            farthestEditedWeekRef.current,
            Number(week)
          )
        }
      })
      if (maxIdx > 0) {
        editedSkusRef.current.set(key, {
          workCentreCode: String(row.workCentreCode),
          skuCode: String(row.skuCode),
          maxWeekEditedIndex: maxIdx,
        })
      }
    })
  }

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleWorkCentreButtonClick}
        >
          {showAllWorkcenters
            ? 'Show all workcenters'
            : 'Show only primary workcenters'}
        </Button>
        <Box mx={1} />

        <Button
          variant="contained"
          color="primary"
          onClick={handleRunPlan}
          disabled={isRunning || !changesApplied}
        >
          {isRunning ? 'Running...' : 'Apply changes'}
        </Button>
      </Box>
      <Box>
        <PlannedProductionTable
          isLoading={isLoading}
          data={tableData ?? []}
          weeks={weeks}
          onDataSaveInTheTable={onDataSaveInTheTable}
          onWeekEdited={handleWeekEdited}
          uploadFile={handleFileUpload}
          isUploading={isUploading}
        />
      </Box>
      <FreezeOptionsDialog
        open={dialogOpen}
        weekOptions={weekOptions}
        initialWeeks={initialWeeksForDialog}
        isSaving={isSaving}
        onCancel={handleDialogClose}
        onConfirm={handleDialogConfirm}
      />
    </Box>
  )
}
